import { Component, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { Header } from '../../shared/header/header';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog';
import { SettingService } from '../setting.service';
import { GlobalConfig, UpdateGlobalConfigRequestModel } from '../setting.type';
import { AuthService } from '../../auth/auth.service';
import { TelegramAlertsPanel } from '../telegram-alerts/telegram-alerts';

type GlobalKey = keyof UpdateGlobalConfigRequestModel;

interface FieldDef {
  key: GlobalKey;
  label: string;
  hint?: string;
  suffix?: string;
  min: number;
  step?: number;
}

interface FieldGroup {
  title: string;
  icon: string;
  fields: FieldDef[];
  /** Cross-field rule for the group; returns a message when the values conflict. */
  validate?: (v: Record<GlobalKey, number>) => string | null;
}

const GROUPS: FieldGroup[] = [
  {
    title: 'Stocks & outsourcing',
    icon: 'inventory_2',
    fields: [
      { key: 'stockMinimumCredit', label: 'Stock minimum credit', suffix: 'coins', min: 0 },
      { key: 'outsourcingMaxPurchasePrice', label: 'Outsourcing max purchase price', hint: 'Multiplier, must be above 0', suffix: '×', min: 0.01, step: 0.01 },
    ],
  },
  {
    title: 'Player pricing',
    icon: 'sports_soccer',
    fields: [
      { key: 'playerMarketPriceAddedValue', label: 'Market price added value', suffix: 'coins', min: 0 },
      { key: 'goldRarePlayerMaxPrice', label: 'Gold rare max price', suffix: 'coins', min: 0 },
      { key: 'goldRareBuyNowPrice', label: 'Gold rare buy-now price', hint: 'Also the minimum credit a stock needs', suffix: 'coins', min: 1 },
      { key: 'playerUpdateOverallMin', label: 'Player update overall min', hint: 'Lowest overall rating the player-update job refreshes', min: 0 },
      { key: 'playerUpdateOverallMax', label: 'Player update overall max', hint: 'Highest overall rating the player-update job refreshes', min: 0 },
    ],
  },
  {
    title: 'Listing & buy contracts',
    icon: 'gavel',
    fields: [
      { key: 'listingDurationSeconds', label: 'Listing duration', suffix: 'sec', min: 1 },
      { key: 'listingMaxCardsPerTuple', label: 'Max cards per tuple', min: 0 },
      { key: 'buyContractStaleTimeoutSeconds', label: 'Buy contract stale timeout', suffix: 'sec', min: 0 },
      { key: 'buyContractMinWindow', label: 'Buy contract min window', suffix: 'cards', min: 1 },
      { key: 'buyContractMaxWindow', label: 'Buy contract max window', hint: 'Must exceed max cards per tuple when that is set', suffix: 'cards', min: 1 },
      { key: 'buyContractMinExpireBufferSeconds', label: 'Min expire buffer', hint: 'Cards expiring sooner than this are not allocated', suffix: 'sec', min: 0 },
      { key: 'buyContractMaxLockExpiredIssues', label: 'Max lock-expired issues', hint: 'Buy contract fails once it reaches this many', suffix: 'issues', min: 1 },
    ],
    validate: v => {
      if (v.buyContractMinWindow > v.buyContractMaxWindow) {
        return 'Buy contract min window must not exceed max window';
      }
      if (v.listingMaxCardsPerTuple > 0 && v.buyContractMaxWindow <= v.listingMaxCardsPerTuple) {
        return 'Buy contract max window must be greater than max cards per tuple';
      }
      return null;
    },
  },
  {
    title: 'Dashboard',
    icon: 'balance',
    fields: [
      { key: 'orderBuyContractRatio', label: 'Orders per buy contract', hint: '2 means 10 active buy contracts call for 20 open orders', suffix: '×', min: 0.1, step: 0.1 },
    ],
  },
  {
    title: 'Supplier tuple reports',
    icon: 'report',
    fields: [
      { key: 'tupleReportThreshold', label: 'Reports to block a tuple', hint: 'Distinct suppliers; then only auto-buy gets it', suffix: 'suppliers', min: 1 },
      { key: 'tupleReportLimit', label: 'Reports per supplier', hint: 'Per window below', min: 1 },
      { key: 'tupleReportWindowMinutes', label: 'Report window', suffix: 'min', min: 1 },
    ],
  },
  {
    title: 'Payments',
    icon: 'account_balance_wallet',
    fields: [
      { key: 'paymentRequestCooldownHours', label: 'Payment request cooldown', suffix: 'hours', min: 1 },
      { key: 'paymentRequestRejectedCooldownHours', label: 'Cooldown after rejection', suffix: 'hours', min: 1 },
    ],
  },
];

function crossFieldValidator(control: AbstractControl): ValidationErrors | null {
  const values = control.value as Record<GlobalKey, number>;
  const errors: Record<string, string> = {};
  for (const g of GROUPS) {
    const message = g.validate?.(values);
    if (message) errors[g.title] = message;
  }
  return Object.keys(errors).length ? { crossField: errors } : null;
}

@Component({
  selector: 'app-settings-page',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    Header,
    TelegramAlertsPanel,
  ],
  templateUrl: './settings-page.html',
  styleUrl: './settings-page.scss',
})
export class SettingsPage implements OnInit {
  private fb = inject(FormBuilder);
  readonly canEdit = inject(AuthService).canWrite('system');
  private settingService = inject(SettingService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  readonly groups = GROUPS;
  loading = true;
  saving = false;
  resetting = false;

  form: FormGroup = this.fb.group(
    Object.fromEntries(
      GROUPS.flatMap(g => g.fields).map(f => [f.key, [f.min, [Validators.required, Validators.min(f.min)]]]),
    ),
    { validators: crossFieldValidator },
  );

  groupError(group: FieldGroup): string | null {
    return this.form.errors?.['crossField']?.[group.title] ?? null;
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.settingService.getConfig()
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: config => this.applyConfig(config),
        error: () => this.snackBar.open('Could not load settings', 'Close', { duration: 5000, panelClass: ['error-snackbar'] }),
      });
  }

  private applyConfig(config: GlobalConfig): void {
    const values: Partial<Record<GlobalKey, unknown>> = {};
    for (const f of GROUPS.flatMap(g => g.fields)) {
      values[f.key] = (config as unknown as Record<string, unknown>)[f.key];
    }
    this.form.reset(values);
  }

  save(): void {
    if (this.form.invalid || !this.form.dirty) return;
    this.saving = true;
    const request = this.form.getRawValue() as UpdateGlobalConfigRequestModel;
    this.settingService.updateGlobalConfig(request)
      .pipe(finalize(() => this.saving = false))
      .subscribe({
        next: () => {
          this.form.markAsPristine();
          this.snackBar.open('Settings saved', 'Close', { duration: 4000 });
        },
        error: (err: HttpErrorResponse) => {
          const message = err.status === 400 && err.error?.error ? err.error.error : 'Could not save settings';
          this.snackBar.open(message, 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
        },
      });
  }

  discard(): void {
    this.load();
  }

  resetToDefaults(): void {
    this.dialog.open(ConfirmDialog, {
      width: '400px',
      data: {
        title: 'Reset settings',
        message: 'Reset every setting to its default value? This cannot be undone.',
      },
    }).afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.resetting = true;
      this.settingService.resetConfig()
        .pipe(finalize(() => this.resetting = false))
        .subscribe({
          next: () => {
            this.snackBar.open('Settings reset to defaults', 'Close', { duration: 4000 });
            this.load();
          },
          error: () => this.snackBar.open('Could not reset settings', 'Close', { duration: 5000, panelClass: ['error-snackbar'] }),
        });
    });
  }
}
